import type { JSX, ReactNode } from "react";
import { cx } from "./classNames";
import styles from "./ui.module.css";

/** Meaning of a badge (design-guidelines.md 1). */
export type BadgeTone = "neutral" | "success" | "info" | "warning" | "danger";

const toneClass: Readonly<Record<BadgeTone, string | undefined>> = {
  neutral: undefined,
  success: styles["badgeSuccess"],
  info: styles["badgeInfo"],
  warning: styles["badgeWarning"],
  danger: styles["badgeDanger"],
};

/** Properties of {@link Badge}. */
export interface BadgeProps {
  /** Meaning: done (success), waiting or planned (info), attention (warning), refused or failed (danger), a plain fact (neutral, the default). */
  readonly tone?: BadgeTone | undefined;
  /** Further class names. */
  readonly className?: string | undefined;
  /** The word or short phrase; it carries the meaning on its own (WCAG 1.4.1). */
  readonly children: ReactNode;
}

/**
 * A short state in a pill (F-30). The tone only helps to scan; every text colour on its surface is
 * a pair of the contrast test.
 *
 * @param props - Component properties.
 * @returns The badge.
 */
export function Badge(props: BadgeProps): JSX.Element {
  const badge = (
    <span className={cx(styles["badge"], toneClass[props.tone ?? "neutral"], props.className)}>
      {props.children}
    </span>
  );

  return badge;
}
